import React, { useEffect, useState } from 'react';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { OVERLAY_PRIORITY, useOverlaySlot } from '@context/overlayGate';

// Kill-switch del PROMPT visible de instalación PWA ("¿Instalar aplicación?" / "Instala Plick
// Comercio…" / "Sí, instalar ahora" + la variante de iOS). Apagado a propósito: con `false` el
// componente no renderiza nada, así que el banner nunca aparece. NO afecta al Service Worker ni al
// registro PWA (los maneja vite-plugin-pwa con registerType 'autoUpdate', independiente de este
// componente) ni al hook usePwaInstall (lo sigue usando NotificationPermissionRequest para
// isIOS/isStandalone). Para RE-ENCENDER el prompt: cambiar a `true` (un solo lugar).
const PWA_INSTALL_PROMPT_ENABLED: boolean = false;

const DISMISSAL_KEY = 'pwa-install-dismissed';
const DISMISSAL_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const DISPLAY_DELAY = 60 * 1000; // 60 seconds

// PWA install vs notification permission are coordinated by the OverlayGate (one interruptive
// prompt at a time), so this component no longer dispatches custom visibility events.
export const PwaInstallPrompt: React.FC = () => {
  const { isInstallable, promptInstall, isIOS, isStandalone } = usePwaInstall();
  const [isVisible, setIsVisible] = useState(false);
  const [showDelayed, setShowDelayed] = useState(false);

  // Check if prompt was dismissed
  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISSAL_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      if (now - dismissedTime < DISMISSAL_DURATION) {
        // Still within dismissal period
        return;
      }
    }

    // Delay showing the prompt
    const timer = setTimeout(() => {
      setShowDelayed(true);
    }, DISPLAY_DELAY);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only show if delayed time has passed and not standalone
    if (showDelayed && !isStandalone) {
      setIsVisible(true);
    }
  }, [showDelayed, isStandalone]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISSAL_KEY, Date.now().toString());
    setIsVisible(false);
  };

  const handleInstall = () => {
    promptInstall();
    setIsVisible(false);
  };

  // OverlayGate: at most one interruptive prompt at a time + yield to incoming orders.
  // Kill-switch (PWA_INSTALL_PROMPT_ENABLED): con el prompt apagado nunca pedimos el slot ni
  // renderizamos el banner; el SW/PWA y usePwaInstall siguen intactos.
  const wantsToShow = PWA_INSTALL_PROMPT_ENABLED && !isStandalone && isVisible && (isIOS || isInstallable);
  const granted = useOverlaySlot('pwa-install', OVERLAY_PRIORITY.systemPrompt, wantsToShow);

  if (!granted) return null;

  // iOS Prompt
  if (isIOS) {
    return (
      <>
        {/* Backdrop for better visibility */}
        <div className="fixed inset-0 bg-black bg-opacity-10 z-[96] pointer-events-none" />

        {/* PWA Install Card - iOS */}
        <div className="fixed bottom-6 left-6 z-[97] max-w-xs w-full pointer-events-auto">
          <div className="bg-color-white rounded-xl shadow-2xl border border-color-gray_light overflow-hidden transform transition-all duration-300 hover:scale-105">
            <div className="bg-color-primary h-1.5 w-full"></div>
            <div className="p-5 relative">
              <button
                onClick={handleDismiss}
                className="absolute top-2 right-2 text-color-gray_dark hover:text-color-gray_ultra_dark focus:outline-none"
                aria-label="Cerrar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 pt-1">
                  <div className="p-2 bg-color-gray_ultra_light rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-color-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-color-primary_dark font-bold text-sm mb-1 pr-4">Instalar App</h3>
                  <p className="text-color-gray_ultra_dark text-xs leading-relaxed mb-2">
                    Para instalar esta app en tu iPhone/iPad:
                  </p>
                  <ol className="text-xs text-color-gray_ultra_dark list-decimal list-inside space-y-1 mb-2">
                    <li>
                      Toca el botón <strong>Compartir</strong> <span className="text-color-secondary text-lg">⏋</span>
                    </li>
                    <li>
                      Selecciona <strong>&quot;Agregar al Inicio&quot;</strong> <span className="text-xl">⊞</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Android / Desktop Prompt - positioned on the LEFT to avoid overlap
  return (
    <>
      {/* Backdrop for better visibility */}
      <div className="fixed inset-0 bg-black bg-opacity-10 z-[96] pointer-events-none" />

      {/* PWA Install Card - Android/Desktop */}
      <div className="fixed bottom-6 left-6 z-[97] max-w-xs w-full pointer-events-auto">
        <div className="bg-color-white rounded-xl shadow-2xl border border-color-gray_light overflow-hidden transform transition-all duration-300 hover:scale-105">
          <div className="bg-color-primary h-1.5 w-full"></div>
          <div className="p-5 relative">
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 text-color-gray_dark hover:text-color-gray_ultra_dark focus:outline-none"
              aria-label="Cerrar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <div className="p-2 bg-color-gray_ultra_light rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-color-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-color-primary_dark font-bold text-sm mb-1 pr-4">¿Instalar aplicación?</h3>
                <p className="text-color-gray_ultra_dark text-xs leading-relaxed mb-4">
                  Instala Plick Comercio para un acceso más rápido y mejor experiencia.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleInstall}
                    className="w-full bg-color-primary text-color-white text-xs font-bold py-2.5 px-4 rounded-lg shadow-md hover:bg-color-primary_dark transition-colors duration-200 focus:outline-none"
                  >
                    Sí, instalar ahora
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
